import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Input } from "@components/inputs/Input/Input";
import { Radio } from "@components/inputs/Radio/Radio";
import { Select } from "@components/inputs/Select/Select";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveCustomTransactionCategoryMutation } from "@services/budget/mutations";
import {
  RemoveBudgetTransactionCategoryType,
  TransactionType,
  type IGetCustomTransactionCategoryResponse,
  type ITransactionCategoriesResponse,
} from "@services/budget/types";

interface IRemoveBudgetTransactionCategoryWithTransactionsModalModalProps
  extends IUseModalProps {
  transactionCategoryData: IGetCustomTransactionCategoryResponse;
  transactionCategories: ITransactionCategoriesResponse;
}

export const RemoveBudgetTransactionCategoryWithTransactionsModal: React.FC<
  IRemoveBudgetTransactionCategoryWithTransactionsModalModalProps
> = (props) => {
  const { transactionCategoryData, transactionCategories, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const [removeType, setRemoveType] =
    useState<RemoveBudgetTransactionCategoryType>(
      RemoveBudgetTransactionCategoryType.ReassignDefaultCategory,
    );

  // TODO: subcategories are not supported here. They are not used right now. Will need API changes.
  const categoryOptions = useMemo(
    () =>
      transactionCategories.transactionCategories
        .filter(
          (category) => category.transactionTypeId === TransactionType.Credit,
        )
        .filter(
          (category) =>
            category.categorySlug.toString() !==
            transactionCategoryData.categorySlug,
        )
        .map((category) => ({
          value: category.transactionCategoryId,
          label:
            category.customerCategoryName ??
            t(`budget.category.${category.categorySlug}`),
        })),
    [
      t,
      transactionCategories.transactionCategories,
      transactionCategoryData.categorySlug,
    ],
  );

  const [newCategory, setNewCategory] = useState<number>(
    categoryOptions[0]!.value,
  );

  const { mutateAsync: removeCustomTransactionCategory, isPending } =
    useRemoveCustomTransactionCategoryMutation();

  const { toastApiError } = useApiErrorUtils();

  const removeCategory = async () => {
    await removeCustomTransactionCategory({
      transactionCategoryId: transactionCategoryData.transactionCategoryId,
      removeType: removeType,
      newTransactionCategoryId:
        removeType === RemoveBudgetTransactionCategoryType.ChangeCategory
          ? newCategory
          : undefined,
    })
      .then(modal.close)
      .catch(toastApiError);
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: BinIcon,
        color: "red",
      }}
      title={t(
        "budget.removeBudgetTransactionCategoryWithTransactionsModal.title",
      )}
      subtitle={t(
        "budget.removeBudgetTransactionCategoryWithTransactionsModal.subtitle",
      )}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" className="w-full" onClick={modal.close}>
            {t(
              "budget.removeBudgetTransactionCategoryWithTransactionsModal.cancel",
            )}
          </Button>
          <Button
            className="w-full"
            onClick={removeCategory}
            isLoading={isPending}
          >
            {t(
              "budget.removeBudgetTransactionCategoryWithTransactionsModal.submit",
            )}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t(
            "budget.removeBudgetTransactionCategoryWithTransactionsModal.name",
          )}
          value={transactionCategoryData.categoryName}
          disabled
        />
        <Radio
          name="removeType"
          onRadioOptionChange={(value) => {
            setRemoveType(value as RemoveBudgetTransactionCategoryType);
          }}
          defaultValue={
            RemoveBudgetTransactionCategoryType.ReassignDefaultCategory
          }
          options={[
            {
              label: t(
                "budget.removeBudgetTransactionCategoryWithTransactionsModal.reassignDefaultCategory",
              ),
              value:
                RemoveBudgetTransactionCategoryType.ReassignDefaultCategory,
            },
            {
              label: t(
                "budget.removeBudgetTransactionCategoryWithTransactionsModal.changeCategory",
              ),
              value: RemoveBudgetTransactionCategoryType.ChangeCategory,
            },
          ]}
        />
        {removeType === RemoveBudgetTransactionCategoryType.ChangeCategory && (
          <Select
            value={newCategory}
            onChange={(value) => {
              setNewCategory(parseInt(value as string));
            }}
            label={t(
              "budget.removeBudgetTransactionCategoryWithTransactionsModal.newCategory",
            )}
            options={categoryOptions}
          />
        )}
      </div>
    </Modal>
  );
};
